import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FiArrowRight, FiEye, FiEyeOff, FiLock, FiMail } from 'react-icons/fi'
import { DContext } from '../../context/DataContext'
import Logo from '../../assets/Logo bg.svg'
import { translateAuthServerMessage } from '../../utils/authServerMessage'
import MetaData from '../../utils/MetaData'

const Login = () => {
    const { t, i18n } = useTranslation()
    const navigate = useNavigate()
    const { setIsAuth, setCurrentUser, BeURL, setLoader, isAuth } = useContext(DContext)

    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [errors, setErrors] = useState({})

    useEffect(() => {
        if (isAuth) {
            navigate('/', { replace: true })
        }
    }, [isAuth, navigate])

    useEffect(() => {
        setErrors({})
    }, [i18n.language])

    const emailError = useMemo(() => {
        if (!email) return ''
        return /^\S+@\S+\.\S+$/.test(email.trim()) ? '' : t('auth.validations.emailInvalid')
    }, [email, t])

    const passwordError = useMemo(() => {
        if (!password) return ''
        return password.trim().length >= 8 ? '' : t('auth.validations.passwordMinLength')
    }, [password, t])

    const handleSubmit = async (e) => {
        e.preventDefault()

        const nextErrors = {}
        if (!email.trim()) nextErrors.email = t('auth.validations.emailRequired')
        if (!password.trim()) nextErrors.password = t('auth.validations.passwordRequired')
        if (email.trim() && !/^\S+@\S+\.\S+$/.test(email.trim())) nextErrors.email = t('auth.validations.emailInvalid')

        setErrors(nextErrors)
        if (Object.keys(nextErrors).length > 0) return

        try {
            setLoader(true)
            const res = await fetch(`${BeURL}/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ email: email.trim().toLowerCase(), password })
            })
            const data = await res.json()
            setLoader(false)

            if (!data.success) {
                alert(translateAuthServerMessage(data.message, t, 'auth.messages.loginFailed'))
                return
            }

            setIsAuth(true)
            setCurrentUser(data.user)
            navigate('/', { replace: true })
        } catch (err) {
            setLoader(false)
            console.log('Error in Login:', err)
            alert(t('auth.messages.serverError'))
        }
    }

    return (
        <>
            <MetaData
                title='Login | Be Alright Krabi'
                description='Login to your Be Alright Krabi account to manage bookings and profile settings.'
                canonicalUrl={`${window.location.origin}/login`}
                robots='noindex,nofollow'
            />
            <div className="relative flex min-h-[85vh] flex-wrap items-center justify-center gap-6 px-4 py-10">
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(23,57,189,0.12),_transparent_40%),radial-gradient(circle_at_bottom_right,_rgba(14,165,233,0.12),_transparent_45%)]" />

                <section className="relative w-full max-w-lg text-center">
                    <img src={Logo} alt="logo" className="mx-auto h-28 w-auto sm:h-36" />
                    <h1 className="mt-3 text-3xl font-black tracking-tight text-[#1739bd] sm:text-5xl">{t('auth.login.title')}</h1>
                    <p className="mt-3 text-sm text-slate-600 sm:text-base">{t('auth.login.subtitle')}</p>
                </section>

                <section className="relative w-full max-w-md rounded-3xl border border-slate-200 bg-white/95 p-5 shadow-[0_18px_55px_rgba(15,23,42,0.12)] backdrop-blur sm:p-6">
                    <h2 className="text-center text-2xl font-extrabold text-[#1739bd]">{t('auth.login.heading')}</h2>
                    <p className="mt-2 text-center text-sm text-slate-600">
                        {t('auth.login.noAccount')}{' '}
                        <Link to="/register" className="font-semibold text-[#1739bd] no-underline hover:text-indigo-700">{t('auth.login.registerLink')}</Link>
                    </p>

                    <form onSubmit={handleSubmit} className="mt-5 space-y-4">
                        <label className="block">
                            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('auth.fields.email')}</span>
                            <div className="flex items-center gap-2 rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2.5 focus-within:border-[#1739bd]">
                                <FiMail className="text-slate-500" />
                                <input
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    type="email"
                                    placeholder="name@mail.com"
                                    className="w-full bg-transparent text-sm outline-none"
                                />
                            </div>
                            {(errors.email || emailError) && <p className="mt-1 text-xs font-medium text-rose-600">{errors.email || emailError}</p>}
                        </label>

                        <label className="block">
                            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('auth.fields.password')}</span>
                            <div className="flex items-center gap-2 rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2.5 focus-within:border-[#1739bd]">
                                <FiLock className="text-slate-500" />
                                <input
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    className="w-full bg-transparent text-sm outline-none"
                                />
                                <button type="button" onClick={() => setShowPassword((prev) => !prev)} className="text-slate-500 transition hover:text-[#1739bd]">
                                    {showPassword ? <FiEyeOff /> : <FiEye />}
                                </button>
                            </div>
                            {(errors.password || passwordError) && <p className="mt-1 text-xs font-medium text-rose-600">{errors.password || passwordError}</p>}
                        </label>

                        <div className="text-right">
                            <Link to="/forgot-password" className="text-xs font-semibold text-[#1739bd] no-underline hover:text-indigo-700">
                                {t('auth.login.forgotPassword')}
                            </Link>
                        </div>

                        <button type="submit" className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#1739bd] px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition hover:bg-indigo-700">
                            <span>{t('auth.login.submit')}</span>
                            <FiArrowRight />
                        </button>
                    </form>
                </section>
            </div>
        </>
    )
}

export default Login